import { CARGA_TRABALHO_INDICATORS_KEY } from "@/core/constants/keys";

export type GanttIndicator =
  "planejamento" | "obra" | "fechamento" | "cancelada" | "indisponibilidade";

export type GanttIndicators = Record<GanttIndicator, boolean>;

export const GANTT_INDICATOR_OPTIONS: { key: GanttIndicator; label: string }[] = [
  { key: "planejamento", label: "Planejamento" },
  { key: "obra", label: "Obra" },
  { key: "fechamento", label: "Fechamento" },
  { key: "cancelada", label: "Cancelada" },
  { key: "indisponibilidade", label: "Indisponibilidade" },
];

/** Cancelada fica oculta por padrão. */
export const DEFAULT_GANTT_INDICATORS: GanttIndicators = {
  planejamento: true,
  obra: true,
  fechamento: true,
  cancelada: false,
  indisponibilidade: true,
};

export const LAST_INDICATOR_MESSAGE =
  "Este é o único indicador ativo. Ative outro antes de ocultá-lo.";

export function countActiveIndicators(indicators: GanttIndicators): number {
  return Object.values(indicators).filter(Boolean).length;
}

/** Pelo menos um indicador precisa continuar ativo. */
export function isLastActiveIndicator(indicators: GanttIndicators, key: GanttIndicator): boolean {
  return indicators[key] && countActiveIndicators(indicators) === 1;
}

/**
 * Alterna um indicador. Desmarcar o último ativo é ignorado: devolve o mesmo
 * estado recebido.
 */
export function toggleIndicator(indicators: GanttIndicators, key: GanttIndicator): GanttIndicators {
  if (isLastActiveIndicator(indicators, key)) return indicators;
  return { ...indicators, [key]: !indicators[key] };
}

function isIndicators(value: unknown): value is GanttIndicators {
  if (typeof value !== "object" || value === null) return false;
  const record = value as Record<string, unknown>;
  return (
    GANTT_INDICATOR_OPTIONS.every(({ key }) => typeof record[key] === "boolean") &&
    countActiveIndicators(record as GanttIndicators) >= 1
  );
}

/** Preferência salva apenas no navegador (sem endpoint). */
export function readStoredIndicators(): GanttIndicators {
  try {
    const raw = localStorage.getItem(CARGA_TRABALHO_INDICATORS_KEY);
    if (!raw) return { ...DEFAULT_GANTT_INDICATORS };
    const parsed: unknown = JSON.parse(raw);
    return isIndicators(parsed) ? { ...parsed } : { ...DEFAULT_GANTT_INDICATORS };
  } catch {
    return { ...DEFAULT_GANTT_INDICATORS };
  }
}

export function persistIndicators(indicators: GanttIndicators): void {
  try {
    localStorage.setItem(CARGA_TRABALHO_INDICATORS_KEY, JSON.stringify(indicators));
  } catch {
    return;
  }
}
